import { useEffect, useState } from 'react';
import { Wrench } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MaintenanceGate — wraps the public app and renders a maintenance screen
 * when the admin has flipped the site_settings.maintenance.enabled flag.
 *
 * Hard rules (defence in depth):
 * - Admin paths (/admin/*) are always reachable so we can switch the flag
 *   off again, even when the gate is on.
 * - The gate NEVER auto-triggers. It only reacts to the explicit admin
 *   toggle stored in site_settings (see /api/public/maintenance) AND only
 *   when the backend payload also reports admin_initiated=true.
 * - URL escape hatch: appending ``?nomaint=1`` to any URL temporarily
 *   bypasses the gate for the current tab (persisted via sessionStorage)
 *   so an admin can always get in even if the API call falsely returns
 *   enabled=true.
 * - Fail-closed on fetch errors: a backend timeout / network error keeps
 *   the gate hidden (initial state is enabled:false), so a flaky API can
 *   never accidentally lock everyone out.
 */
export default function MaintenanceGate({ children }) {
  const [maint, setMaint] = useState({ enabled: false, message: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // URL escape hatch — persists for the rest of the tab session.
    try {
      const params = new URLSearchParams(window.location.search || '');
      if (params.get('nomaint') === '1') sessionStorage.setItem('emina_nomaint', '1');
    } catch { /* ignore */ }

    const path = window.location.pathname || '/';
    if (path.startsWith('/admin')) { setLoading(false); return; }

    let cancelled = false;
    async function load() {
      try {
        const r = await fetch(`${API}/public/maintenance`, { cache: 'no-store' });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) {
          // Both flags must be set — defence against a stale "enabled=true"
          // row without an admin_initiated marker. The backend watchdog
          // should auto-clear those, but render defensively in case the
          // client's response was already cached before the revert.
          const safeEnabled = !!d.enabled && (d.admin_initiated === undefined || !!d.admin_initiated);
          setMaint({ enabled: safeEnabled, message: d.message || '' });
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (loading) return children;
  const path = window.location.pathname || '/';
  let bypassed = false;
  try { bypassed = sessionStorage.getItem('emina_nomaint') === '1'; } catch { /* ignore */ }
  if (path.startsWith('/admin') || bypassed || !maint.enabled) return children;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 text-white px-6" data-testid="maintenance-gate">
      <div className="max-w-lg w-full text-center bg-gray-900 border border-amber-600/40 rounded-2xl p-10">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/15 text-amber-400 mb-4">
          <Wrench size={28} />
        </div>
        <h1 className="text-2xl font-bold mb-2">Portal je u održavanju</h1>
        <p className="text-gray-400 text-sm" data-testid="maintenance-message">{maint.message}</p>
        <p className="text-xs text-gray-500 mt-6">Vraćamo se vrlo brzo. Hvala na strpljenju.</p>
        <p className="text-[10px] text-gray-600 mt-8">
          Admin? Pristupite na <a href="/admin/login" className="underline hover:text-amber-400">/admin/login</a>
        </p>
      </div>
    </div>
  );
}
